<template>
  <div class="amazon-reconciliation w-full">
    <!-- Estado del catalogo: es contexto para cualquier pestana (cuando se
         sincronizo por ultima vez, que quedo sin enviar), asi que vive fuera de
         los tabs. Los botones que actuan sobre eso estan en la pestana Sync. -->
    <div
      class="flex flex-row flex-wrap items-center justify-between gap-x-6 gap-y-1 text-base text-gray-500 mb-3"
    >
      <div class="flex flex-row flex-wrap items-center gap-x-6 gap-y-1">
        <span>
          <template v-if="summary && summary.lastSyncedAt">
            {{ $t("message.marketplaces.catalog_last_sync") }}:
            {{ summary.lastSyncedAt }}
          </template>
          <template v-else>
            {{ $t("message.marketplaces.catalog_never_synced") }}
          </template>
        </span>
        <span v-if="pendingChanges" class="text-gray-700">
          <span class="font-semibold">{{ pendingChanges }}</span>
          {{ $t("message.marketplaces.changes_pending") }}
        </span>
      </div>
      <div v-if="syncing || bulkSyncing" class="flex flex-row items-center">
        <BaseMiniSpinner class="mr-2" />
        {{ $t("message.marketplaces.catalog_syncing") }}
      </div>
    </div>

    <!-- Tabs: cada funcionalidad ocupa la pantalla entera en vez de apilarse. -->
    <div class="flex flex-row flex-wrap border-b mb-6">
      <button
        v-for="t in tabs"
        :key="t.value"
        class="px-4 py-2 -mb-px border-b-2 font-semibold"
        :class="
          tab === t.value
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        "
        @click="tab = t.value"
      >
        {{ t.label }}
        <span
          v-if="t.badge"
          class="ml-1 px-2 py-0.5 rounded-full text-sm bg-gray-200 text-gray-700"
          >{{ t.badge }}</span
        >
      </button>
    </div>

    <template v-if="tab === 'cross'">
      <BaseLoading v-if="loadingSummary && !summary" />

      <template v-if="summary">
        <!-- Totales de cada lado -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="border rounded-lg bg-white p-5">
            <div class="text-base text-gray-500 uppercase tracking-wide">
              {{ $t("message.marketplaces.catalog_in_amazon") }}
            </div>
            <div class="text-4xl font-bold mt-1">
              {{ summary.amazon.total }}
            </div>
            <div class="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-base">
              <span
                ><span class="font-semibold">{{ summary.amazon.fba }}</span>
                FBA</span
              >
              <span
                ><span class="font-semibold">{{ summary.amazon.fbm }}</span>
                FBM</span
              >
              <span class="text-green-600"
                ><span class="font-semibold">{{ summary.amazon.active }}</span>
                {{ $t("message.marketplaces.catalog_active") }}</span
              >
              <a
                class="text-gray-500 underline cursor-pointer"
                :class="bucket === 'inactive' ? 'font-semibold' : ''"
                @click="selectBucket('inactive')"
                ><span class="font-semibold">{{
                  summary.amazon.inactive
                }}</span>
                {{ $t("message.marketplaces.catalog_inactive") }}</a
              >
              <span
                ><span class="font-semibold">{{
                  summary.amazon.withStock
                }}</span>
                {{ $t("message.marketplaces.catalog_with_stock") }}</span
              >
            </div>
            <div class="flex h-2 mt-4 rounded overflow-hidden bg-gray-200">
              <div
                class="bg-orange-400"
                :style="{
                  width: pct(summary.amazon.fba, summary.amazon.total)
                }"
                :title="`FBA: ${summary.amazon.fba}`"
              ></div>
              <div
                class="bg-blue-400"
                :style="{
                  width: pct(summary.amazon.fbm, summary.amazon.total)
                }"
                :title="`FBM: ${summary.amazon.fbm}`"
              ></div>
            </div>
          </div>

          <div class="border rounded-lg bg-white p-5">
            <div class="text-base text-gray-500 uppercase tracking-wide">
              {{ $t("message.marketplaces.catalog_in_local") }}
            </div>
            <div class="text-4xl font-bold mt-1">{{ summary.local.total }}</div>
            <div class="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-base">
              <span
                ><span class="font-semibold">{{ summary.local.fba }}</span>
                FBA</span
              >
              <span
                ><span class="font-semibold">{{ summary.local.fbm }}</span>
                FBM</span
              >
              <span
                ><span class="font-semibold">{{ summary.local.enabled }}</span>
                {{ $t("message.marketplaces.catalog_export_enabled") }}</span
              >
            </div>
            <div class="flex h-2 mt-4 rounded overflow-hidden bg-gray-200">
              <div
                class="bg-orange-400"
                :style="{ width: pct(summary.local.fba, summary.local.total) }"
                :title="`FBA: ${summary.local.fba}`"
              ></div>
              <div
                class="bg-blue-400"
                :style="{ width: pct(summary.local.fbm, summary.local.total) }"
                :title="`FBM: ${summary.local.fbm}`"
              ></div>
            </div>
          </div>
        </div>

        <!-- Barra visual del cruce -->
        <div class="border rounded-lg bg-white p-5 mb-4">
          <div class="text-base text-gray-500 uppercase tracking-wide mb-3">
            {{ $t("message.marketplaces.catalog_crossing") }}
          </div>
          <div class="flex h-6 rounded overflow-hidden bg-gray-200 text-sm">
            <div
              class="bg-green-500 flex items-center justify-center text-white font-semibold"
              :style="{ width: pct(summary.matched, crossTotal) }"
              :title="
                `${$t('message.marketplaces.catalog_matched')}: ${
                  summary.matched
                }`
              "
            >
              <span v-if="share(summary.matched) > 8">{{
                summary.matched
              }}</span>
            </div>
            <div
              class="bg-yellow-500 flex items-center justify-center text-yellow-900 font-semibold"
              :style="{ width: pct(summary.onlyAmazon.total, crossTotal) }"
              :title="
                `${$t('message.marketplaces.catalog_only_amazon')}: ${
                  summary.onlyAmazon.total
                }`
              "
            >
              <span v-if="share(summary.onlyAmazon.total) > 8">{{
                summary.onlyAmazon.total
              }}</span>
            </div>
            <div
              class="bg-red-500 flex items-center justify-center text-white font-semibold"
              :style="{ width: pct(summary.onlyLocal, crossTotal) }"
              :title="
                `${$t('message.marketplaces.catalog_only_local')}: ${
                  summary.onlyLocal
                }`
              "
            >
              <span v-if="share(summary.onlyLocal) > 8">{{
                summary.onlyLocal
              }}</span>
            </div>
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-base">
            <span class="flex items-center"
              ><span class="w-3 h-3 rounded-sm bg-green-500 mr-2"></span
              >{{ $t("message.marketplaces.catalog_matched") }}</span
            >
            <span class="flex items-center"
              ><span class="w-3 h-3 rounded-sm bg-yellow-500 mr-2"></span
              >{{ $t("message.marketplaces.catalog_only_amazon") }}</span
            >
            <span class="flex items-center"
              ><span class="w-3 h-3 rounded-sm bg-red-500 mr-2"></span
              >{{ $t("message.marketplaces.catalog_only_local") }}</span
            >
          </div>
        </div>

        <!-- Tarjetas clicables: cada una abre su detalle -->
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6"
        >
          <div
            v-for="card in cards"
            :key="card.bucket"
            class="border rounded-lg bg-white p-5 cursor-pointer transition"
            :class="
              bucket === card.bucket
                ? card.activeClass + ' border-2'
                : 'hover:shadow-md'
            "
            @click="selectBucket(card.bucket)"
          >
            <div class="flex items-center">
              <span class="w-3 h-3 rounded-full mr-2" :class="card.dot"></span>
              <span class="text-base text-gray-600">{{ card.label }}</span>
            </div>
            <div class="text-3xl font-bold mt-2" :class="card.textClass">
              {{ card.value }}
            </div>
            <div v-if="card.hint" class="text-sm text-gray-500 mt-1">
              {{ card.hint }}
            </div>
          </div>
        </div>

        <!-- Detalle del bucket seleccionado -->
        <div class="border rounded-lg bg-white p-5">
          <div
            class="flex flex-col xl:flex-row xl:items-center justify-between mb-4"
          >
            <div class="font-semibold">
              {{ bucketLabel }}
              <span class="text-gray-500 font-normal">({{ detailTotal }})</span>
            </div>
            <div
              class="flex flex-row flex-wrap items-center gap-y-2 mt-3 xl:mt-0"
            >
              <input
                v-model="search"
                type="text"
                class="border rounded px-3 h-10 mr-3"
                :placeholder="$t('message.marketplaces.catalog_search')"
                @input="onSearch"
              />
              <!-- Prioridad = unidades vendidas en los ultimos 12 meses: lo que
                 mas se mueve se atiende primero. -->
              <select
                v-model="sort"
                class="border rounded px-2 h-10 mr-3"
                @change="applyFilters"
              >
                <option value="">
                  {{ $t("message.marketplaces.catalog_sort_default") }}
                </option>
                <option value="priority">
                  {{ $t("message.marketplaces.catalog_sort_priority") }}
                </option>
              </select>
              <label
                v-if="bucket === 'fba_check'"
                class="flex flex-row items-center mr-3 text-base cursor-pointer"
              >
                <input
                  v-model="onlyDiff"
                  type="checkbox"
                  class="mr-2"
                  @change="applyFilters"
                />
                {{ $t("message.marketplaces.catalog_only_diff") }}
              </label>
              <select
                v-if="bucket !== 'fba_check'"
                v-model="channelFilter"
                class="border rounded px-2 h-10 mr-3"
                @change="applyFilters"
              >
                <option value="">
                  {{ $t("message.marketplaces.catalog_filter_channel") }}
                </option>
                <option value="fba">FBA</option>
                <option value="fbm">FBM</option>
              </select>
              <select
                v-if="bucket !== 'fba_check'"
                v-model="stockFilter"
                class="border rounded px-2 h-10 mr-3"
                @change="applyFilters"
              >
                <option value="">
                  {{ $t("message.marketplaces.catalog_filter_stock") }}
                </option>
                <option value="with">
                  {{ $t("message.marketplaces.catalog_with_stock") }}
                </option>
                <option value="without">
                  {{ $t("message.marketplaces.catalog_without_stock") }}
                </option>
              </select>
              <select
                v-if="bucket !== 'only_amazon'"
                v-model="verifiedFilter"
                class="border rounded px-2 h-10 mr-3"
                @change="applyFilters"
              >
                <option value="">
                  {{ $t("message.marketplaces.catalog_filter_verified") }}
                </option>
                <option value="no">
                  {{ $t("message.marketplaces.catalog_not_verified") }}
                </option>
                <option value="yes">
                  {{ $t("message.marketplaces.catalog_verified") }}
                </option>
              </select>
              <template
                v-if="bucket !== 'only_local' && bucket !== 'fba_check'"
              >
                <select
                  v-model="statusFilter"
                  class="border rounded px-2 h-10 mr-3"
                  @change="applyFilters"
                >
                  <option value="">
                    {{ $t("message.marketplaces.catalog_filter_status") }}
                  </option>
                  <option value="active">
                    {{ $t("message.marketplaces.catalog_active") }}
                  </option>
                  <option value="inactive">
                    {{ $t("message.marketplaces.catalog_inactive") }}
                  </option>
                </select>
              </template>
              <button
                v-if="canPush"
                class="btn flex flex-row items-center mr-3"
                :class="
                  bulkSyncing || !detailTotal
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-info-button'
                "
                :disabled="bulkSyncing || !detailTotal"
                :title="$t('message.marketplaces.catalog_direction_out')"
                @click="bulkSyncAll"
              >
                <BaseMiniSpinner v-if="bulkSyncing" class="mr-2" />
                {{
                  $t("message.marketplaces.catalog_sync_all", {
                    count: detailTotal
                  })
                }}
              </button>
              <button
                class="btn flex flex-row items-center mr-3"
                :class="
                  downloading || !detailTotal
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-dark-button'
                "
                :disabled="downloading || !detailTotal"
                @click="downloadList"
              >
                <BaseMiniSpinner v-if="downloading" class="mr-2" />
                {{ $t("message.marketplaces.catalog_download_list") }}
              </button>
              <button
                v-if="bucket === 'only_amazon' && canEdit"
                class="btn flex flex-row items-center"
                :class="
                  importing || !summary.onlyAmazon.importable
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-success-button'
                "
                :disabled="importing || !summary.onlyAmazon.importable"
                @click="importAll"
              >
                <BaseMiniSpinner v-if="importing" class="mr-2" />
                {{
                  $t("message.marketplaces.catalog_import_all", {
                    count: summary.onlyAmazon.importable
                  })
                }}
              </button>
              <button
                v-if="bucket === 'only_local' && canEdit"
                class="btn flex flex-row items-center"
                :class="
                  deleting || !summary.onlyLocal
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-error-button'
                "
                :disabled="deleting || !summary.onlyLocal"
                @click="deleteAll"
              >
                <BaseMiniSpinner v-if="deleting" class="mr-2" />
                {{
                  $t("message.marketplaces.catalog_delete_all", {
                    count: summary.onlyLocal
                  })
                }}
              </button>
            </div>
          </div>

          <div v-if="canPush" class="text-sm text-gray-500 mb-3">
            {{ $t("message.marketplaces.catalog_direction_hint") }}
          </div>
          <div
            v-if="bucket === 'fba_check' && !fbaCheck.hasFbaData"
            class="mb-4 text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
          >
            {{ $t("message.marketplaces.catalog_fba_no_data") }}
          </div>
          <div v-if="loadingDetail" class="py-8 flex justify-center">
            <BaseMiniSpinner />
          </div>
          <div
            v-else-if="!detail.length"
            class="py-8 text-center text-gray-500"
          >
            {{ $t("message.marketplaces.catalog_empty") }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-base">
              <thead>
                <tr class="border-b text-left text-gray-600">
                  <th class="py-2 pr-3">SKU</th>
                  <th class="py-2 pr-3">
                    {{ $t("message.marketplaces.product_title") }}
                  </th>
                  <th class="py-2 pr-3">ASIN</th>
                  <th class="py-2 pr-3">NKR</th>
                  <th
                    class="py-2 pr-3 text-center"
                    :title="$t('message.marketplaces.catalog_priority_hint')"
                  >
                    {{ $t("message.marketplaces.catalog_priority") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.catalog_channel") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.catalog_stock_nkr") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.catalog_stock_fba_infoauto") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.catalog_stock_fba_amazon") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.publishable_price") }}
                  </th>
                  <th class="py-2 pr-3 text-center">
                    {{ $t("message.marketplaces.catalog_state") }}
                  </th>
                  <th v-if="showActions" class="py-2 text-center">
                    {{ $t("message.marketplaces.actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in detail"
                  :key="row.sku"
                  class="border-b hover:bg-gray-50"
                >
                  <td class="py-2 pr-3 font-mono whitespace-nowrap">
                    <template v-if="bucket !== 'only_local'">
                      <a
                        :href="amazonEditUrl(row.sku)"
                        target="_blank"
                        rel="noopener"
                        class="text-blue-600 hover:underline"
                        :title="
                          $t('message.marketplaces.catalog_edit_in_amazon')
                        "
                        >{{ row.sku }}</a
                      >
                      <a
                        :href="amazonSearchUrl(row.sku)"
                        target="_blank"
                        rel="noopener"
                        class="ml-2 text-gray-500 hover:text-gray-700"
                        :title="
                          $t('message.marketplaces.catalog_find_in_amazon')
                        "
                        ><i class="mdi mdi-magnify text-lg"></i
                      ></a>
                    </template>
                    <span v-else>{{ row.sku }}</span>
                  </td>
                  <td class="py-2 pr-3" :title="row.title">
                    <div class="truncate max-w-xs">{{ row.title || "—" }}</div>
                  </td>
                  <td class="py-2 pr-3 font-mono">{{ row.asin || "—" }}</td>
                  <td class="py-2 pr-3 font-mono">
                    <a
                      v-if="row.nkrCode"
                      :href="nkrProductUrl(row.nkrCode)"
                      target="_blank"
                      rel="noopener"
                      class="text-blue-600 hover:underline"
                      :title="$t('message.marketplaces.catalog_open_in_nkr')"
                      >{{ row.nkrCode }}</a
                    >
                    <span v-else class="text-gray-500">—</span>
                  </td>
                  <td
                    class="py-2 pr-3 text-center"
                    :class="
                      row.soldLastYear > 0 ? 'font-semibold' : 'text-gray-500'
                    "
                    :title="$t('message.marketplaces.catalog_priority_hint')"
                  >
                    {{ row.soldLastYear || 0 }}
                  </td>
                  <td class="py-2 pr-3 text-center">
                    <span
                      v-if="bucket === 'channel_mismatch'"
                      class="text-red-600"
                    >
                      {{ row.fba ? "FBA" : "FBM" }} →
                      {{ amazonChannel(row.fulfillmentChannel) }}
                    </span>
                    <span v-else>{{
                      row.fulfillmentChannel
                        ? amazonChannel(row.fulfillmentChannel)
                        : row.fba
                        ? "FBA"
                        : "FBM"
                    }}</span>
                  </td>
                  <td
                    class="py-2 pr-3 text-center"
                    :class="row.localStock > 0 ? '' : 'text-gray-500'"
                  >
                    {{
                      row.localStock !== null && row.localStock !== undefined
                        ? row.localStock
                        : 0
                    }}
                  </td>
                  <td
                    class="py-2 pr-3 text-center"
                    :class="
                      fbaMismatch(row) ? 'text-red-600 font-semibold' : ''
                    "
                  >
                    {{
                      row.infoautoFbaStock !== null &&
                      row.infoautoFbaStock !== undefined
                        ? row.infoautoFbaStock
                        : "—"
                    }}
                  </td>
                  <td
                    class="py-2 pr-3 text-center"
                    :class="
                      fbaMismatch(row) ? 'text-red-600 font-semibold' : ''
                    "
                  >
                    <!-- En FBA el stock lo tiene Amazon: viene de FBA Inventory,
                       no del reporte de listings (que solo trae quantity FBM). -->
                    <template v-if="isFbaRow(row)">
                      <span
                        v-if="row.fbaQuantity !== null"
                        class="font-semibold"
                      >
                        {{ row.fbaQuantity }}
                      </span>
                      <span
                        v-else
                        class="text-gray-500"
                        :title="
                          $t('message.marketplaces.catalog_stock_fba_hint')
                        "
                        >—</span
                      >
                      <span
                        v-if="row.fbaInboundQuantity > 0"
                        class="text-sm text-blue-600 ml-1"
                        :title="$t('message.marketplaces.catalog_fba_inbound')"
                        >+{{ row.fbaInboundQuantity }}</span
                      >
                    </template>
                    <span v-else>{{
                      row.quantity !== null && row.quantity !== undefined
                        ? row.quantity
                        : "—"
                    }}</span>
                  </td>
                  <td class="py-2 pr-3 text-center">
                    {{ row.price !== null ? `$${row.price}` : "—" }}
                  </td>
                  <td class="py-2 pr-3 text-center">
                    <span
                      class="px-2 py-0.5 rounded text-sm"
                      :class="
                        row.status === 'Active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      "
                    >
                      {{
                        row.status ||
                          $t("message.marketplaces.catalog_not_in_amazon")
                      }}
                    </span>
                    <i
                      v-if="row.verifiedAt"
                      class="mdi mdi-check-decagram text-green-600 ml-1"
                      :title="
                        $t('message.marketplaces.catalog_verified_on', {
                          date: row.verifiedAt
                        })
                      "
                    ></i>
                  </td>
                  <td
                    v-if="showActions"
                    class="py-2 text-center whitespace-nowrap"
                  >
                    <!-- La llave abre el editor completo del listing: imagenes,
                       ficha de Amazon, verificacion y bitacora. Solo tiene
                       sentido si la pieza existe de nuestro lado. -->
                    <button
                      v-if="row.nkrCode"
                      class="text-gray-500 hover:text-blue-600 mr-2"
                      :title="$t('message.marketplaces.editor_open')"
                      @click="openEditor(row)"
                    >
                      <i class="mdi mdi-wrench text-lg"></i>
                    </button>
                    <template v-if="bucket === 'only_amazon'">
                      <button
                        v-if="row.importable"
                        class="btn btn-success-button py-1 px-2 text-sm"
                        :disabled="importing"
                        @click="importOne(row.sku)"
                      >
                        {{ $t("message.marketplaces.catalog_import") }}
                      </button>
                      <span
                        v-else-if="!row.nkrCode"
                        class="text-sm text-gray-500"
                      >
                        {{
                          $t("message.marketplaces.catalog_no_local_product")
                        }}
                      </span>
                    </template>
                    <button
                      v-else-if="bucket === 'only_local'"
                      class="btn btn-error-button py-1 px-2 text-sm"
                      :disabled="deleting"
                      @click="deleteOne(row.sku)"
                    >
                      {{ $t("message.marketplaces.catalog_delete") }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginado -->
          <div
            v-if="totalPages > 1"
            class="flex flex-row justify-center items-center mt-6"
          >
            <button
              class="btn btn-dark-button mr-4"
              :disabled="page <= 1"
              @click="page > 1 && (page--, loadDetail())"
            >
              {{ $t("message.marketplaces.previous") }}
            </button>
            <div>{{ page }} / {{ totalPages }}</div>
            <button
              class="btn btn-dark-button ml-4"
              :disabled="page >= totalPages"
              @click="page < totalPages && (page++, loadDetail())"
            >
              {{ $t("message.marketplaces.next") }}
            </button>
          </div>
        </div>
      </template>
    </template>

    <template v-else-if="tab === 'unpublished'">
      <!-- No publicados: piezas con stock que todavia no existen en Amazon.
           No salen del snapshot (ahi no estan), sino del reporte de publicables,
           que ya trae las ventas del ultimo ano. -->
      <div class="border rounded-lg bg-white p-5">
        <div
          class="flex flex-col sm:flex-row sm:items-center justify-between mb-4"
        >
          <div>
            <div class="font-semibold">
              {{ $t("message.marketplaces.unpublished_title") }}
              <span v-if="unpublishedLoaded" class="text-gray-500 font-normal"
                >({{ unpublishedFiltered.length }})</span
              >
            </div>
            <div class="text-sm text-gray-500 mt-1">
              {{ $t("message.marketplaces.unpublished_hint") }}
            </div>
          </div>
          <div
            class="flex flex-row flex-wrap items-center gap-y-2 mt-3 sm:mt-0"
          >
            <input
              v-model="unpublishedSearch"
              type="text"
              class="border rounded px-3 h-10 mr-3"
              :placeholder="$t('message.marketplaces.catalog_search')"
            />
            <select
              v-model="unpublishedSort"
              class="border rounded px-2 h-10 mr-3"
            >
              <option value="sold">
                {{ $t("message.marketplaces.catalog_sort_priority") }}
              </option>
              <option value="price_desc">
                {{ $t("message.marketplaces.unpublished_sort_price_desc") }}
              </option>
              <option value="price_asc">
                {{ $t("message.marketplaces.unpublished_sort_price_asc") }}
              </option>
              <option value="stock">
                {{ $t("message.marketplaces.unpublished_sort_stock") }}
              </option>
            </select>
            <button
              class="btn btn-dark-button flex flex-row items-center"
              :class="
                loadingUnpublished || !unpublishedFiltered.length
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              "
              :disabled="loadingUnpublished || !unpublishedFiltered.length"
              @click="downloadUnpublished"
            >
              {{ $t("message.marketplaces.catalog_download_list") }}
            </button>
          </div>
        </div>

        <div v-if="loadingUnpublished" class="py-8 flex justify-center">
          <BaseMiniSpinner />
        </div>
        <div
          v-else-if="!unpublishedFiltered.length"
          class="py-8 text-center text-gray-500"
        >
          {{ $t("message.marketplaces.publishable_empty") }}
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-base">
            <thead>
              <tr class="border-b text-left text-gray-600">
                <th class="py-2 pr-3">NKR</th>
                <th class="py-2 pr-3">
                  {{ $t("message.marketplaces.product_title") }}
                </th>
                <th
                  class="py-2 pr-3 text-center"
                  :title="$t('message.marketplaces.catalog_priority_hint')"
                >
                  {{ $t("message.marketplaces.catalog_priority") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.catalog_stock_nkr") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.publishable_price") }}
                </th>
                <th class="py-2 text-center">
                  {{ $t("message.marketplaces.actions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in unpublishedPage"
                :key="row.nkrCode"
                class="border-b hover:bg-gray-50"
              >
                <td class="py-2 pr-3 font-mono">
                  <a
                    :href="nkrProductUrl(row.nkrCode)"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                    >{{ row.nkrCode }}</a
                  >
                </td>
                <td class="py-2 pr-3" :title="row.title">
                  <div class="truncate max-w-xs">{{ row.title || "—" }}</div>
                </td>
                <td
                  class="py-2 pr-3 text-center"
                  :class="row.sold > 0 ? 'font-semibold' : 'text-gray-500'"
                >
                  {{ row.sold || 0 }}
                </td>
                <td class="py-2 pr-3 text-center">{{ row.stock }}</td>
                <td class="py-2 pr-3 text-center">
                  {{ row.calculatedPrice ? `$${row.calculatedPrice}` : "—" }}
                </td>
                <td class="py-2 text-center">
                  <a
                    :href="nkrProductUrl(row.nkrCode)"
                    target="_blank"
                    rel="noopener"
                    class="btn btn-success-button py-1 px-2 text-sm"
                    >{{ $t("message.marketplaces.publish") }}</a
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="unpublishedPages > 1"
          class="flex flex-row justify-center items-center mt-6"
        >
          <button
            class="btn btn-dark-button mr-4"
            :disabled="unpublishedPageNum <= 1"
            @click="unpublishedPageNum > 1 && unpublishedPageNum--"
          >
            {{ $t("message.marketplaces.previous") }}
          </button>
          <div>{{ unpublishedPageNum }} / {{ unpublishedPages }}</div>
          <button
            class="btn btn-dark-button ml-4"
            :disabled="unpublishedPageNum >= unpublishedPages"
            @click="
              unpublishedPageNum < unpublishedPages && unpublishedPageNum++
            "
          >
            {{ $t("message.marketplaces.next") }}
          </button>
        </div>
      </div>
    </template>

    <!-- Oportunidades FBA: lo que ya se vende por FBM y Amazon todavia no
         despacha. -->
    <template v-else-if="tab === 'fba'">
      <div class="border rounded-lg bg-white p-5">
        <div class="font-semibold mb-1">
          {{ $t("message.marketplaces.fba_opp_title") }}
        </div>
        <p class="text-base text-gray-500 mb-4">
          {{ $t("message.marketplaces.fba_opp_hint") }}
        </p>

        <div class="flex flex-row flex-wrap items-center gap-y-2 mb-4">
          <input
            v-model="fbaOppSearch"
            type="text"
            class="border rounded px-3 h-10 mr-3"
            :placeholder="$t('message.marketplaces.catalog_search')"
            @input="onFbaOppSearch"
          />
          <select
            v-model="fbaOppDays"
            class="border rounded px-2 h-10 mr-3"
            @change="reloadFbaOpportunities"
          >
            <option value="90">
              {{ $t("message.marketplaces.fba_opp_days_90") }}
            </option>
            <option value="180">
              {{ $t("message.marketplaces.fba_opp_days_180") }}
            </option>
            <option value="365">
              {{ $t("message.marketplaces.fba_opp_days_365") }}
            </option>
          </select>
          <select
            v-model="fbaOppSort"
            class="border rounded px-2 h-10 mr-3"
            @change="reloadFbaOpportunities"
          >
            <option value="">
              {{ $t("message.marketplaces.catalog_sort_priority") }}
            </option>
            <option value="price_desc">
              {{ $t("message.marketplaces.unpublished_sort_price_desc") }}
            </option>
            <option value="price_asc">
              {{ $t("message.marketplaces.unpublished_sort_price_asc") }}
            </option>
          </select>
          <label
            class="flex flex-row items-center mr-3 text-base cursor-pointer"
          >
            <input
              v-model="fbaOppOnlyStock"
              type="checkbox"
              class="mr-2"
              @change="reloadFbaOpportunities"
            />
            {{ $t("message.marketplaces.fba_opp_only_stock") }}
          </label>
          <button
            class="btn btn-dark-button flex flex-row items-center"
            :class="
              loadingFbaOpp || !fbaOppTotal
                ? 'opacity-50 cursor-not-allowed'
                : ''
            "
            :disabled="loadingFbaOpp || !fbaOppTotal"
            @click="downloadFbaOpportunities"
          >
            {{ $t("message.marketplaces.catalog_download_list") }}
          </button>
        </div>

        <div v-if="loadingFbaOpp" class="py-8 flex justify-center">
          <BaseMiniSpinner />
        </div>
        <div
          v-else-if="!fbaOpportunities.length"
          class="py-8 text-center text-gray-500"
        >
          {{ $t("message.marketplaces.fba_opp_empty") }}
        </div>
        <div v-else class="overflow-x-auto">
          <div class="text-base text-gray-500 mb-2">
            {{
              $t("message.marketplaces.fba_opp_count", { count: fbaOppTotal })
            }}
          </div>
          <table class="w-full text-base">
            <thead>
              <tr class="border-b text-left text-gray-600">
                <th class="py-2 pr-3">SKU</th>
                <th class="py-2 pr-3">
                  {{ $t("message.marketplaces.product_title") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.fba_opp_sold_window") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.catalog_priority") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.catalog_stock_nkr") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.publishable_price") }}
                </th>
                <th class="py-2 pr-3 text-center">
                  {{ $t("message.marketplaces.catalog_state") }}
                </th>
                <th v-if="canEdit" class="py-2 text-center">
                  {{ $t("message.marketplaces.actions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in fbaOpportunities"
                :key="row.sku"
                class="border-b hover:bg-gray-50"
              >
                <td class="py-2 pr-3 font-mono whitespace-nowrap">
                  <a
                    :href="amazonEditUrl(row.sku)"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                    >{{ row.sku }}</a
                  >
                </td>
                <td class="py-2 pr-3" :title="row.title">
                  <div class="truncate max-w-xs">{{ row.title || "—" }}</div>
                </td>
                <td class="py-2 pr-3 text-center font-semibold">
                  {{ row.soldLastQuarter || 0 }}
                </td>
                <td class="py-2 pr-3 text-center">
                  {{ row.soldLastYear || 0 }}
                </td>
                <td
                  class="py-2 pr-3 text-center"
                  :class="row.localStock > 0 ? '' : 'text-gray-500'"
                >
                  {{ row.localStock || 0 }}
                </td>
                <td class="py-2 pr-3 text-center">
                  {{ row.price !== null ? `$${row.price}` : "—" }}
                </td>
                <td class="py-2 pr-3 text-center">
                  <span
                    class="px-2 py-0.5 rounded text-sm"
                    :class="
                      row.status === 'Active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    "
                    >{{ row.status || "—" }}</span
                  >
                </td>
                <td v-if="canEdit" class="py-2 text-center">
                  <button
                    v-if="row.nkrCode"
                    class="text-gray-500 hover:text-blue-600"
                    :title="$t('message.marketplaces.editor_open')"
                    @click="openEditor(row)"
                  >
                    <i class="mdi mdi-wrench text-lg"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="fbaOppPages > 1"
          class="flex flex-row justify-center items-center mt-6"
        >
          <button
            class="btn btn-dark-button mr-4"
            :disabled="fbaOppPage <= 1"
            @click="fbaOppPage > 1 && (fbaOppPage--, loadFbaOpportunities())"
          >
            {{ $t("message.marketplaces.previous") }}
          </button>
          <div>{{ fbaOppPage }} / {{ fbaOppPages }}</div>
          <button
            class="btn btn-dark-button ml-4"
            :disabled="fbaOppPage >= fbaOppPages"
            @click="
              fbaOppPage < fbaOppPages && (fbaOppPage++, loadFbaOpportunities())
            "
          >
            {{ $t("message.marketplaces.next") }}
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="tab === 'sync'">
      <!-- Barra de acción: última sincronización + botón de sync -->
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between bg-white border rounded-lg px-5 py-4 mb-6"
      >
        <div>
          <div class="text-lg font-semibold">
            {{ $t("message.marketplaces.catalog_title") }}
          </div>
          <div class="text-base text-gray-500 mt-1">
            <template v-if="summary && summary.lastSyncedAt">
              {{ $t("message.marketplaces.catalog_last_sync") }}:
              {{ summary.lastSyncedAt }}
            </template>
            <template v-else>
              {{ $t("message.marketplaces.catalog_never_synced") }}
            </template>
          </div>
          <div
            v-if="syncMessage"
            class="text-base mt-1"
            :class="syncMessageClass"
          >
            {{ syncMessage }}
          </div>
        </div>
        <div class="mt-3 sm:mt-0">
          <div
            class="text-sm text-gray-500 uppercase tracking-wide mb-1 sm:text-right"
          >
            {{ $t("message.marketplaces.catalog_direction_in") }}
          </div>
          <div class="flex flex-row flex-wrap items-center gap-2">
            <button
              class="btn btn-dark-button flex flex-row items-center justify-center"
              :class="refreshingFba ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="refreshingFba"
              @click="refreshFba"
            >
              <BaseMiniSpinner v-if="refreshingFba" class="mr-2" />
              {{ $t("message.marketplaces.catalog_refresh_fba") }}
            </button>
            <button
              class="btn flex flex-row items-center justify-center"
              :class="
                syncing
                  ? 'btn-dark-button opacity-50 cursor-not-allowed'
                  : 'btn-success-button'
              "
              :disabled="syncing"
              @click="startSync"
            >
              <BaseMiniSpinner v-if="syncing" class="mr-2" />
              {{
                syncing
                  ? $t("message.marketplaces.catalog_syncing")
                  : $t("message.marketplaces.catalog_sync_now")
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Salida: enviar a Amazon solo lo que cambió de precio o stock -->
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between bg-white border rounded-lg px-5 py-4 mb-6"
      >
        <div>
          <div class="text-sm text-gray-500 uppercase tracking-wide mb-1">
            {{ $t("message.marketplaces.catalog_direction_out") }}
          </div>
          <div class="text-lg font-semibold">
            {{ $t("message.marketplaces.changes_title") }}
          </div>
          <div class="text-base text-gray-500 mt-1">
            <template v-if="loadingPending">
              {{ $t("message.marketplaces.changes_loading") }}
            </template>
            <template v-else-if="pendingChanges === null">
              {{ $t("message.marketplaces.changes_hint") }}
            </template>
            <template v-else-if="pendingChanges === 0">
              {{ $t("message.marketplaces.changes_none") }}
            </template>
            <template v-else>
              <span class="font-semibold text-gray-700">{{
                pendingChanges
              }}</span>
              {{ $t("message.marketplaces.changes_pending") }}
            </template>
          </div>
          <div
            v-if="changesMessage"
            class="text-base mt-1"
            :class="changesError ? 'text-red-600' : 'text-green-600'"
          >
            {{ changesMessage }}
          </div>
        </div>
        <div class="mt-3 sm:mt-0 flex flex-row flex-wrap items-center gap-2">
          <button
            class="btn btn-dark-button flex flex-row items-center justify-center"
            :class="loadingPending ? 'opacity-50 cursor-not-allowed' : ''"
            :disabled="loadingPending"
            @click="loadPendingChanges"
          >
            <BaseMiniSpinner v-if="loadingPending" class="mr-2" />
            {{ $t("message.marketplaces.changes_check") }}
          </button>
          <button
            class="btn flex flex-row items-center justify-center"
            :class="
              sendingChanges || pendingChanges === 0
                ? 'btn-dark-button opacity-50 cursor-not-allowed'
                : 'btn-success-button'
            "
            :disabled="sendingChanges || pendingChanges === 0"
            @click="sendChanges"
          >
            <BaseMiniSpinner v-if="sendingChanges" class="mr-2" />
            {{
              sendingChanges
                ? $t("message.marketplaces.changes_sending")
                : $t("message.marketplaces.changes_send_now")
            }}
          </button>
          <!-- Alternativa cuando hace falta reenviar todo (no solo lo cambiado):
             un listing corregido a mano en Amazon, o dudas de que el catalogo
             publicado refleje nuestros precios. -->
          <button
            v-if="canEdit"
            class="btn flex flex-row items-center justify-center"
            :class="
              bulkSyncing || !registeredTotal
                ? 'btn-dark-button opacity-50 cursor-not-allowed'
                : 'btn-info-button'
            "
            :disabled="bulkSyncing || !registeredTotal"
            :title="$t('message.marketplaces.catalog_direction_out')"
            @click="bulkSyncAllRegistered"
          >
            <BaseMiniSpinner v-if="bulkSyncing" class="mr-2" />
            {{
              $t("message.marketplaces.catalog_sync_all", {
                count: registeredTotal
              })
            }}
          </button>
        </div>
      </div>
    </template>

    <!-- Editor del listing: se monta bajo demanda para no cargar imágenes ni
         bitácora de productos que nadie abrió. -->
    <AmazonListingEditor
      v-if="editorRow"
      :nkr-code="editorRow.nkrCode"
      :sku="editorRow.sku"
      @close="editorRow = null"
      @updated="onEditorUpdated"
    />

    <!-- Confirmación explícita: es la única acción que escribe en Amazon. -->
    <BaseModal id="amazon-sync-confirm" v-model="showSyncConfirm">
      <template #header>
        <div class="font-semibold">
          {{ $t("message.marketplaces.catalog_sync_confirm_title") }}
        </div>
      </template>
      <div class="flex flex-col p-2">
        <div
          class="border-l-4 border-yellow-400 bg-yellow-50 text-base px-4 py-3 mb-4"
        >
          {{ $t("message.marketplaces.catalog_sync_confirm_warning") }}
        </div>

        <table class="text-base mb-4">
          <tr>
            <td class="pr-4 py-1 text-gray-500">
              {{ $t("message.marketplaces.catalog_sync_confirm_scope") }}
            </td>
            <td class="py-1 font-semibold">{{ syncConfirmScopeLabel }}</td>
          </tr>
          <tr>
            <td class="pr-4 py-1 text-gray-500">
              {{ $t("message.marketplaces.catalog_sync_confirm_count") }}
            </td>
            <td class="py-1 font-semibold">{{ syncConfirmCount }}</td>
          </tr>
          <tr v-if="activeFiltersLabel && syncScope !== 'all'">
            <td class="pr-4 py-1 text-gray-500">
              {{ $t("message.marketplaces.catalog_sync_confirm_filters") }}
            </td>
            <td class="py-1">{{ activeFiltersLabel }}</td>
          </tr>
        </table>

        <ul class="text-base text-gray-600 list-disc pl-5 mb-6">
          <li>{{ $t("message.marketplaces.catalog_sync_confirm_price") }}</li>
          <li>{{ $t("message.marketplaces.catalog_sync_confirm_stock") }}</li>
          <li>{{ $t("message.marketplaces.catalog_sync_confirm_fba") }}</li>
        </ul>

        <div class="flex flex-row justify-end">
          <button
            class="btn btn-dark-button mr-3"
            :disabled="bulkSyncing"
            @click="showSyncConfirm = false"
          >
            {{ $t("message.marketplaces.catalog_cancel") }}
          </button>
          <button
            class="btn flex flex-row items-center"
            :class="
              bulkSyncing
                ? 'btn-dark-button opacity-50 cursor-not-allowed'
                : 'btn-info-button'
            "
            :disabled="bulkSyncing"
            @click="runBulkSync"
          >
            <BaseMiniSpinner v-if="bulkSyncing" class="mr-2" />
            {{ $t("message.marketplaces.catalog_sync_confirm_ok") }}
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { debounce } from "lodash";
import {
  startAmazonCatalogSync,
  getAmazonCatalogStatus,
  getAmazonReconciliation,
  getAmazonReconciliationItems,
  importAmazonCatalogItems,
  deleteAmazonOnlyLocalItems,
  refreshAmazonFbaInventory,
  bulkSyncToAmazon,
  syncAmazonChanges,
  getAmazonPendingChanges,
  getAmazonJob,
  getAmazonFbaOpportunities,
  getAmazonPublishable
} from "@/api/store/MarketplacesItemsService.js";
import AmazonListingEditor from "@/components/product/AmazonListingEditor.vue";

// Seller Central de US: el marketplace en el que operamos.
const SELLER_CENTRAL = "https://sellercentral.amazon.com";

export default {
  name: "AmazonCatalogReconciliation",
  components: { AmazonListingEditor },
  data() {
    return {
      summary: null,
      loadingSummary: true,
      syncing: false,
      refreshingFba: false,
      syncMessage: null,
      syncError: false,
      // Envío de cambios (nosotros -> Amazon). null = todavía no se consultó.
      pendingChanges: null,
      loadingPending: false,
      sendingChanges: false,
      changesMessage: null,
      changesError: false,
      pollTimer: null,
      bucket: "only_amazon",
      detail: [],
      loadingDetail: false,
      importing: false,
      deleting: false,
      downloading: false,
      search: "",
      channelFilter: "",
      stockFilter: "",
      statusFilter: "",
      verifiedFilter: "",
      // "" = orden natural del bucket; "priority" = ventas de 12 meses.
      sort: "",
      onlyDiff: false,
      bulkSyncing: false,
      bulkPollTimer: null,
      showSyncConfirm: false,
      // Fila cuyo editor está abierto (null = cerrado).
      editorRow: null,
      // "bucket" = lo que muestra la tabla con sus filtros; "all" = todos los
      // productos mapeados en Amazon.
      syncScope: "bucket",
      page: 1,
      limitPerPage: 25,
      detailTotal: 0,
      totalPages: 1,
      // Pestana visible. Cada una carga lo suyo la primera vez que se abre.
      tab: "cross",
      // No publicados: el reporte es una sola consulta local y no cambia entre
      // paginas, asi que se trae entero y se filtra/ordena en el navegador.
      unpublishedLoaded: false,
      loadingUnpublished: false,
      unpublished: [],
      unpublishedSearch: "",
      unpublishedSort: "sold",
      unpublishedPageNum: 1,
      unpublishedPerPage: 25,
      // Oportunidades FBA: paginado en servidor, como el resto del cruce.
      loadingFbaOpp: false,
      fbaOpportunities: [],
      fbaOppLoaded: false,
      fbaOppSearch: "",
      fbaOppDays: "90",
      fbaOppSort: "",
      fbaOppOnlyStock: true,
      fbaOppPage: 1,
      fbaOppTotal: 0,
      fbaOppPages: 1
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters["auth/userHasPermission"](
        "can_edit_marketplaces_items"
      );
    },
    // Universo del cruce: coincidentes + los dos lados sueltos.
    crossTotal() {
      if (!this.summary) return 0;
      return (
        this.summary.matched +
        this.summary.onlyAmazon.total +
        this.summary.onlyLocal
      );
    },
    syncMessageClass() {
      return this.syncError ? "text-red-600" : "text-blue-600";
    },
    cards() {
      if (!this.summary) return [];
      return [
        {
          bucket: "matched",
          label: this.$t("message.marketplaces.catalog_matched"),
          value: this.summary.matched,
          dot: "bg-green-500",
          textClass: "text-green-600",
          activeClass: "border-green-500"
        },
        {
          bucket: "only_amazon",
          label: this.$t("message.marketplaces.catalog_only_amazon"),
          value: this.summary.onlyAmazon.total,
          hint: this.$t("message.marketplaces.catalog_importable_hint", {
            importable: this.summary.onlyAmazon.importable,
            unknown: this.summary.onlyAmazon.unknown
          }),
          dot: "bg-yellow-500",
          textClass: "text-yellow-600",
          activeClass: "border-yellow-500"
        },
        {
          bucket: "only_local",
          label: this.$t("message.marketplaces.catalog_only_local"),
          value: this.summary.onlyLocal,
          hint: this.$t("message.marketplaces.catalog_only_local_hint"),
          dot: "bg-red-500",
          textClass: "text-red-600",
          activeClass: "border-red-500"
        },
        {
          bucket: "channel_mismatch",
          label: this.$t("message.marketplaces.catalog_channel_mismatch"),
          value: this.summary.channelMismatch,
          hint: this.$t("message.marketplaces.catalog_channel_mismatch_hint"),
          dot: "bg-blue-500",
          textClass: "text-blue-600",
          activeClass: "border-blue-500"
        },
        {
          bucket: "fba_check",
          label: this.$t("message.marketplaces.catalog_fba_check"),
          value: this.fbaCheck.diff,
          hint: this.$t("message.marketplaces.catalog_fba_check_hint", {
            total: this.fbaCheck.total,
            amazon: this.fbaCheck.amazonUnits,
            infoauto: this.fbaCheck.infoautoUnits
          }),
          dot: "bg-purple-500",
          textClass: "text-purple-600",
          activeClass: "border-purple-500"
        }
      ];
    },
    fbaCheck() {
      return (
        (this.summary && this.summary.fbaCheck) || {
          total: 0,
          ok: 0,
          diff: 0,
          onlyAmazon: 0,
          onlyInfoauto: 0,
          amazonUnits: 0,
          infoautoUnits: 0,
          hasFbaData: false
        }
      );
    },
    // Solo se puede enviar a Amazon lo que ya está registrado de nuestro lado:
    // en only_amazon el item aún no existe en marketplaces_items (hay que
    // importarlo antes).
    // Lo que el usuario tiene filtrado, para que el modal no mienta sobre el
    // alcance real del envío.
    activeFiltersLabel() {
      const parts = [];
      if (this.search) {
        parts.push(`"${this.search}"`);
      }
      if (this.channelFilter) {
        parts.push(this.channelFilter.toUpperCase());
      }
      if (this.stockFilter) {
        parts.push(
          this.stockFilter === "with"
            ? this.$t("message.marketplaces.catalog_with_stock")
            : this.$t("message.marketplaces.catalog_without_stock")
        );
      }
      if (this.statusFilter && this.bucket !== "only_local") {
        parts.push(
          this.statusFilter === "active"
            ? this.$t("message.marketplaces.catalog_active")
            : this.$t("message.marketplaces.catalog_inactive")
        );
      }
      if (this.verifiedFilter && this.bucket !== "only_amazon") {
        parts.push(
          this.verifiedFilter === "yes"
            ? this.$t("message.marketplaces.catalog_verified")
            : this.$t("message.marketplaces.catalog_not_verified")
        );
      }
      if (this.bucket === "fba_check" && this.onlyDiff) {
        parts.push(this.$t("message.marketplaces.catalog_only_diff"));
      }
      if (this.sort === "priority") {
        parts.push(this.$t("message.marketplaces.catalog_sort_priority"));
      }
      return parts.join(" · ");
    },
    canPush() {
      return this.canEdit && this.bucket !== "only_amazon";
    },
    // only_amazon importa y only_local elimina; en cualquier bucket, la llave
    // abre el editor del listing, así que la columna está siempre que se pueda
    // editar.
    showActions() {
      return this.canEdit;
    },
    bucketLabel() {
      const card = this.cards.find(c => c.bucket === this.bucket);
      if (card) return card.label;
      // 'inactive' no tiene tarjeta: se abre desde el contador de Amazon.
      if (this.bucket === "inactive") {
        return this.$t("message.marketplaces.catalog_inactive_title");
      }
      return "";
    },
    // Productos ya mapeados en Amazon (marketplaces_items), independiente del
    // bucket que esté mostrando la tabla.
    registeredTotal() {
      return (
        (this.summary && this.summary.local && this.summary.local.total) || 0
      );
    },
    // El modal se comparte entre los dos botones: el alcance y el conteo cambian
    // según cuál lo abrió.
    syncConfirmScopeLabel() {
      return this.syncScope === "all"
        ? this.$t("message.marketplaces.catalog_sync_scope_all")
        : this.bucketLabel;
    },
    syncConfirmCount() {
      return this.syncScope === "all" ? this.registeredTotal : this.detailTotal;
    },
    tabs() {
      return [
        {
          value: "cross",
          label: this.$t("message.marketplaces.catalog_crossing")
        },
        {
          value: "unpublished",
          label: this.$t("message.marketplaces.unpublished_title"),
          // Solo cuando ya se cargo: un 0 mientras tanto se lee como "no hay".
          badge: this.unpublishedLoaded ? this.unpublished.length : null
        },
        {
          value: "fba",
          label: this.$t("message.marketplaces.fba_opp_title"),
          badge: this.fbaOppLoaded ? this.fbaOppTotal : null
        },
        {
          value: "sync",
          label: this.$t("message.marketplaces.tab_sync"),
          badge: this.pendingChanges || null
        }
      ];
    },
    // Busqueda y orden de no publicados: en cliente, sobre el reporte completo.
    unpublishedFiltered() {
      const term = this.unpublishedSearch.trim().toLowerCase();
      let rows = this.unpublished;
      if (term) {
        rows = rows.filter(
          r =>
            (r.nkrCode || "").toLowerCase().includes(term) ||
            (r.title || "").toLowerCase().includes(term)
        );
      }
      const num = v => (v === null || v === undefined ? 0 : Number(v) || 0);
      const sorted = [...rows];
      if (this.unpublishedSort === "price_desc") {
        sorted.sort((a, b) => num(b.calculatedPrice) - num(a.calculatedPrice));
      } else if (this.unpublishedSort === "price_asc") {
        sorted.sort((a, b) => num(a.calculatedPrice) - num(b.calculatedPrice));
      } else if (this.unpublishedSort === "stock") {
        sorted.sort((a, b) => num(b.stock) - num(a.stock));
      } else {
        // Prioridad: ventas del ultimo ano; a igualdad, lo que mas stock tiene.
        sorted.sort(
          (a, b) => num(b.sold) - num(a.sold) || num(b.stock) - num(a.stock)
        );
      }
      return sorted;
    },
    unpublishedPages() {
      return (
        Math.ceil(this.unpublishedFiltered.length / this.unpublishedPerPage) ||
        1
      );
    },
    unpublishedPage() {
      const start = (this.unpublishedPageNum - 1) * this.unpublishedPerPage;
      return this.unpublishedFiltered.slice(
        start,
        start + this.unpublishedPerPage
      );
    }
  },
  watch: {
    // Cada pestana pide sus datos la primera vez que se abre: entrar al
    // componente no dispara tres consultas de las que quiza no se use ninguna.
    tab(value) {
      if (value === "unpublished" && !this.unpublishedLoaded) {
        this.loadUnpublished();
      }
      if (value === "fba" && !this.fbaOppLoaded) {
        this.loadFbaOpportunities();
      }
    },
    // Cambiar el filtro o el orden deja la pagina actual sin sentido.
    unpublishedSearch() {
      this.unpublishedPageNum = 1;
    },
    unpublishedSort() {
      this.unpublishedPageNum = 1;
    }
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.bulkPollTimer) clearInterval(this.bulkPollTimer);
  },
  mounted() {
    this.loadSummary();
    this.loadDetail();
    this.loadPendingChanges();
    // Si quedó un reporte en curso de una sesión anterior, retomamos el polling.
    getAmazonCatalogStatus().then(res => {
      if (res && res.status === "processing") {
        this.syncing = true;
        this.syncMessage = this.$t("message.marketplaces.catalog_syncing");
        this.startPolling(res.jobId);
      }
    });
  },
  methods: {
    // Porcentaje como string CSS; evita división por cero.
    pct(value, total) {
      if (!total) return "0%";
      return ((value / total) * 100).toFixed(2) + "%";
    },
    // Porcentaje numérico sobre el cruce, para decidir si cabe la etiqueta.
    share(value) {
      if (!this.crossTotal) return 0;
      return (value / this.crossTotal) * 100;
    },
    // Editor del listing en Seller Central, directo por SKU del seller.
    amazonEditUrl(sku) {
      return `${SELLER_CENTRAL}/skucentral?mSku=${encodeURIComponent(sku)}`;
    },
    // Alternativa: el gestor de inventario ya filtrado por ese SKU.
    amazonSearchUrl(sku) {
      return `${SELLER_CENTRAL}/inventory?searchTerm=${encodeURIComponent(
        sku
      )}`;
    },
    // Ficha de la pieza en el propio panel, para comparar en otra pestaña.
    nkrProductUrl(nkrCode) {
      return this.$router.resolve({
        name: "ProductPage",
        params: { productCode: nkrCode }
      }).href;
    },
    // Discrepancia entre nuestro almacén 'FBA' de infoauto y lo que Amazon tiene.
    fbaMismatch(row) {
      if (!this.fbaCheck.hasFbaData || !this.isFbaRow(row)) return false;
      const amazon = row.fbaQuantity || 0;
      const infoauto = row.infoautoFbaStock || 0;
      return amazon !== infoauto;
    },
    isFbaRow(row) {
      if (row.fulfillmentChannel) return row.fulfillmentChannel !== "DEFAULT";
      return !!row.fba;
    },
    // 'DEFAULT' es el canal FBM en los reportes de Amazon.
    amazonChannel(channel) {
      if (!channel) return "—";
      return channel === "DEFAULT" ? "FBM" : "FBA";
    },
    openEditor(row) {
      this.editorRow = row;
    },
    // El editor pudo enviar el SKU a Amazon o cambiar su verificación: se
    // refrescan los totales y la tabla sin cerrar el editor.
    onEditorUpdated() {
      this.loadSummary();
      this.loadDetail();
      this.loadPendingChanges();
    },
    async loadSummary() {
      this.loadingSummary = true;
      const res = await getAmazonReconciliation().catch(() => null);
      if (res) this.summary = res;
      this.loadingSummary = false;
    },
    selectBucket(bucket) {
      if (this.bucket === bucket) return;
      this.bucket = bucket;
      this.page = 1;
      // El filtro de estado se solapa con el bucket 'inactive'; lo limpiamos
      // para no mostrar un cruce contradictorio.
      if (bucket === "inactive") this.statusFilter = "";
      this.loadDetail();
    },
    onSearch: debounce(function() {
      this.page = 1;
      this.loadDetail();
    }, 300),
    applyFilters() {
      this.page = 1;
      this.loadDetail();
    },
    // Única acción que ESCRIBE en Amazon: manda precio (y stock si es FBM) de
    // TODO el bucket con los filtros actuales, vía Feeds API.
    // Botón de la tabla: sincroniza el bucket con los filtros aplicados.
    bulkSyncAll() {
      if (this.bulkSyncing || !this.detailTotal) return;
      this.syncScope = "bucket";
      this.showSyncConfirm = true;
    },
    // Botón de la sección de cambios: todos los productos que ya tenemos
    // mapeados en Amazon, sin depender del bucket ni de los filtros de la tabla.
    bulkSyncAllRegistered() {
      if (this.bulkSyncing || !this.registeredTotal) return;
      this.syncScope = "all";
      this.showSyncConfirm = true;
    },
    async runBulkSync() {
      if (this.bulkSyncing) return;
      const syncingAll = this.syncScope === "all";
      if (!syncingAll && !this.detailTotal) return;
      this.bulkSyncing = true;

      // Alcance "all": el backend resuelve la lista con el reporte completo de
      // Amazon (resolveCodes con type 'all'), así no hay que traer 5.000 códigos
      // al navegador para reenviarlos.
      let payload = { filters: { type: "all" } };

      if (!syncingAll) {
        // La tabla está paginada: pedimos el bucket entero para saber qué códigos
        // entran, en vez de mandar solo la página visible.
        const all = await getAmazonReconciliationItems({
          ...this.detailParams(),
          limitPerPage: 0
        }).catch(() => null);
        const codes = [
          ...new Set(
            ((all && all.products) || [])
              .filter(r => r.nkrCode && r.registered !== 0)
              .map(r => r.nkrCode)
          )
        ];
        if (!codes.length) {
          this.bulkSyncing = false;
          this.showSyncConfirm = false;
          this.$toast.open({
            message: this.$t("message.marketplaces.catalog_empty"),
            type: "error",
            position: "top-right"
          });
          return;
        }
        payload = { codes };
      }

      bulkSyncToAmazon(payload)
        .then(res => {
          this.showSyncConfirm = false;
          this.$toast.open({
            message: this.$t("message.marketplaces.bulk_sync_started", {
              count: res.totalItems
            }),
            type: "success",
            position: "top-right"
          });
          // El envío movió el last_synced_at de los items, así que lo que
          // figuraba como pendiente ya no lo está.
          this.loadPendingChanges();
          if (res.jobId) this.pollBulkJob(res.jobId);
          else this.bulkSyncing = false;
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
          this.bulkSyncing = false;
        });
    },
    pollBulkJob(jobId) {
      if (this.bulkPollTimer) clearInterval(this.bulkPollTimer);
      this.bulkPollTimer = setInterval(() => {
        getAmazonJob(jobId)
          .then(job => {
            if (!job || job.status === "done" || job.status === "error") {
              clearInterval(this.bulkPollTimer);
              this.bulkPollTimer = null;
              this.bulkSyncing = false;
              const done = job && job.status === "done";
              this.$toast.open({
                message: done
                  ? this.$t("message.marketplaces.sync_success")
                  : this.$t("message.marketplaces.sync_error"),
                type: done ? "success" : "error",
                position: "top-right"
              });
              if (done) this.loadDetail();
            }
          })
          .catch(() => {
            clearInterval(this.bulkPollTimer);
            this.bulkPollTimer = null;
            this.bulkSyncing = false;
          });
      }, 5000);
    },
    // Filtros comunes al listado y a la descarga.
    detailParams() {
      return {
        bucket: this.bucket,
        search: this.search,
        channel: this.channelFilter,
        stock: this.stockFilter,
        status: this.bucket === "only_local" ? "" : this.statusFilter,
        // El estado de verificación es nuestro, no de Amazon: en only_amazon no
        // hay registro local al que aplicarlo.
        verified: this.bucket === "only_amazon" ? "" : this.verifiedFilter,
        diff: this.bucket === "fba_check" && this.onlyDiff ? 1 : "",
        sort: this.sort
      };
    },

    async loadDetail() {
      this.loadingDetail = true;
      const res = await getAmazonReconciliationItems({
        ...this.detailParams(),
        page: this.page,
        limitPerPage: this.limitPerPage
      }).catch(() => null);
      this.detail = (res && res.products) || [];
      this.detailTotal = (res && res.totalItems) || 0;
      this.totalPages = (res && res.totalPages) || 1;
      this.loadingDetail = false;
    },
    // Cuántos SKU cambiaron de precio o stock desde su última sincronización.
    // Solo cuenta, no envía nada.
    loadPendingChanges() {
      if (this.loadingPending) return;
      this.loadingPending = true;
      this.changesMessage = null;
      return getAmazonPendingChanges()
        .then(res => {
          this.pendingChanges = (res && res.total) || 0;
        })
        .catch(e => {
          this.changesError = true;
          this.changesMessage =
            (e && e.response && e.response.data) ||
            this.$t("message.marketplaces.sync_error");
        })
        .finally(() => {
          this.loadingPending = false;
        });
    },
    // Envía a Amazon únicamente lo que cambió. Es la misma operación que corre
    // por cron (cronjobs amazon_sync_changed_products); el botón sirve para no
    // esperar al próximo ciclo.
    sendChanges() {
      if (this.sendingChanges) return;
      this.sendingChanges = true;
      this.changesMessage = null;
      this.changesError = false;
      syncAmazonChanges()
        .then(res => {
          if (res && res.skipped) {
            this.pendingChanges = 0;
            this.changesMessage = this.$t("message.marketplaces.changes_none");
            return;
          }
          this.pendingChanges = 0;
          this.changesMessage = this.$t("message.marketplaces.changes_sent", {
            count: (res && res.totalItems) || 0
          });
          this.$toast.open({
            message: this.changesMessage,
            type: "success",
            position: "top-right"
          });
          // El feed queda en proceso del lado de Amazon: se refleja en el
          // reporte cuando termine, igual que un bulk manual.
          this.loadSummary();
        })
        .catch(e => {
          this.changesError = true;
          this.changesMessage =
            (e && e.response && e.response.data) ||
            this.$t("message.marketplaces.sync_error");
          this.$toast.open({
            message: this.changesMessage,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.sendingChanges = false;
        });
    },
    // El stock FBA no viene en el reporte de listings: se consulta aparte y
    // tarda segundos, así que no necesita el polling del reporte.
    refreshFba() {
      if (this.refreshingFba) return;
      this.refreshingFba = true;
      refreshAmazonFbaInventory()
        .then(res => {
          this.$toast.open({
            message: this.$t("message.marketplaces.catalog_fba_refreshed", {
              count: res.updated
            }),
            type: "success",
            position: "top-right"
          });
          this.loadDetail();
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.refreshingFba = false;
        });
    },
    startSync() {
      if (this.syncing) return;
      this.syncing = true;
      this.syncError = false;
      this.syncMessage = this.$t("message.marketplaces.catalog_requesting");
      startAmazonCatalogSync()
        .then(res => {
          this.syncMessage = this.$t("message.marketplaces.catalog_syncing");
          this.startPolling(res.jobId);
        })
        .catch(e => {
          this.syncing = false;
          this.syncError = true;
          this.syncMessage =
            (e && e.response && e.response.data) ||
            this.$t("message.marketplaces.sync_error");
        });
    },
    // Amazon tarda desde segundos hasta varios minutos en generar el reporte.
    startPolling(jobId) {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = setInterval(() => {
        getAmazonCatalogStatus(jobId)
          .then(res => {
            if (!res) return;
            if (res.status === "done" || res.status === "error") {
              clearInterval(this.pollTimer);
              this.pollTimer = null;
              this.syncing = false;
              this.syncError = res.status === "error";
              this.syncMessage = res.message;
              this.loadSummary();
              this.loadDetail();
            }
          })
          .catch(() => {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.syncing = false;
            this.syncError = true;
            this.syncMessage = this.$t("message.marketplaces.sync_error");
          });
      }, 5000);
    },
    importAll() {
      this.runImport(null);
    },
    importOne(sku) {
      this.runImport([sku]);
    },
    runImport(skus) {
      if (this.importing) return;
      this.importing = true;
      importAmazonCatalogItems(skus)
        .then(res => {
          this.$toast.open({
            message: this.$t("message.marketplaces.catalog_imported", {
              count: res.imported
            }),
            type: "success",
            position: "top-right"
          });
          this.loadSummary();
          this.loadDetail();
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.importing = false;
        });
    },
    deleteAll() {
      if (!this.summary.onlyLocal) return;
      const ok = window.confirm(
        this.$t("message.marketplaces.catalog_delete_all_confirm", {
          count: this.summary.onlyLocal
        })
      );
      if (!ok) return;
      this.runDelete(null);
    },
    deleteOne(sku) {
      const ok = window.confirm(
        this.$t("message.marketplaces.catalog_delete_confirm", { sku })
      );
      if (!ok) return;
      this.runDelete([sku]);
    },
    runDelete(skus) {
      if (this.deleting) return;
      this.deleting = true;
      deleteAmazonOnlyLocalItems(skus)
        .then(res => {
          this.$toast.open({
            message: this.$t("message.marketplaces.catalog_deleted", {
              count: res.deleted
            }),
            type: "success",
            position: "top-right"
          });
          // Tras borrar, el paginado cambia: en masivo volvemos al inicio y en
          // individual retrocedemos si la página quedó vacía.
          if (!skus) this.page = 1;
          else if (this.page > 1 && this.detail.length === 1) this.page--;
          this.loadSummary();
          this.loadDetail();
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    // ----- No publicados con stock -----
    async loadUnpublished() {
      if (this.loadingUnpublished) return;
      this.loadingUnpublished = true;
      // limitPerPage 0 => el backend devuelve el reporte entero sin paginar.
      const res = await getAmazonPublishable({ limitPerPage: 0 }).catch(
        () => null
      );
      this.unpublished = (res && res.products) || [];
      this.unpublishedLoaded = true;
      this.unpublishedPageNum = 1;
      this.loadingUnpublished = false;
    },
    downloadUnpublished() {
      const rows = this.unpublishedFiltered;
      if (!rows.length) return;
      this.writeCsv(
        ["nkr_code", "title", "brand", "sold_last_year", "stock", "price"],
        rows.map(r => [
          r.nkrCode,
          r.title,
          r.brand,
          r.sold,
          r.stock,
          r.calculatedPrice
        ]),
        "amazon_no_publicados"
      );
    },

    // ----- Oportunidades FBA -----
    fbaOppParams() {
      return {
        search: this.fbaOppSearch,
        days: this.fbaOppDays,
        sort: this.fbaOppSort,
        stock: this.fbaOppOnlyStock ? "with" : ""
      };
    },
    reloadFbaOpportunities() {
      this.fbaOppPage = 1;
      this.loadFbaOpportunities();
    },
    onFbaOppSearch: debounce(function() {
      this.reloadFbaOpportunities();
    }, 300),
    async loadFbaOpportunities() {
      this.loadingFbaOpp = true;
      const res = await getAmazonFbaOpportunities({
        ...this.fbaOppParams(),
        page: this.fbaOppPage,
        limitPerPage: this.limitPerPage
      }).catch(() => null);
      this.fbaOpportunities = (res && res.products) || [];
      this.fbaOppTotal = (res && res.totalItems) || 0;
      this.fbaOppPages = (res && res.totalPages) || 1;
      this.fbaOppLoaded = true;
      this.loadingFbaOpp = false;
    },
    async downloadFbaOpportunities() {
      if (this.loadingFbaOpp || !this.fbaOppTotal) return;
      this.loadingFbaOpp = true;
      const res = await getAmazonFbaOpportunities({
        ...this.fbaOppParams(),
        limitPerPage: 0
      }).catch(() => null);
      this.loadingFbaOpp = false;
      const rows = (res && res.products) || [];
      if (!rows.length) return;
      this.writeCsv(
        [
          "sku",
          "nkr_code",
          "title",
          "asin",
          "sold_window",
          "sold_last_year",
          "stock_nkr",
          "price",
          "status"
        ],
        rows.map(r => [
          r.sku,
          r.nkrCode,
          r.title,
          r.asin,
          r.soldLastQuarter,
          r.soldLastYear,
          r.localStock,
          r.price,
          r.status
        ]),
        `amazon_oportunidades_fba_${this.fbaOppDays}d`
      );
    },

    // Descarga el bucket completo (no solo la página visible) como CSV.
    async downloadList() {
      if (this.downloading) return;
      this.downloading = true;
      // limitPerPage 0 => el backend devuelve el bucket sin paginar.
      const res = await getAmazonReconciliationItems({
        ...this.detailParams(),
        limitPerPage: 0
      }).catch(() => null);
      this.downloading = false;
      const rows = (res && res.products) || [];
      if (!rows.length) return;
      this.exportCsv(rows);
    },
    exportCsv(rows) {
      this.writeCsv(
        [
          "sku",
          "nkr_code",
          "title",
          "asin",
          "channel",
          "stock_nkr",
          "quantity_amazon",
          "stock_fba_infoauto",
          "stock_fba_amazon",
          "inbound_fba",
          "price",
          "status",
          "sold_last_year"
        ],
        rows.map(row => [
          row.sku,
          row.nkrCode,
          row.title,
          row.asin,
          row.fulfillmentChannel
            ? this.amazonChannel(row.fulfillmentChannel)
            : row.fba
            ? "FBA"
            : "FBM",
          row.localStock === null || row.localStock === undefined
            ? 0
            : row.localStock,
          row.quantity,
          row.infoautoFbaStock,
          row.fbaQuantity,
          row.fbaInboundQuantity,
          row.price,
          row.status,
          row.soldLastYear
        ]),
        `amazon_${this.bucket}`
      );
    },
    // Arma el CSV y dispara la descarga. Las filas llegan crudas, en el mismo
    // orden que las cabeceras.
    writeCsv(headers, rows, name) {
      const cell = value => {
        const v = value === null || value === undefined ? "" : String(value);
        return `"${v.replace(/"/g, '""')}"`;
      };
      const lines = [headers.join(",")].concat(
        rows.map(row => row.map(cell).join(","))
      );
      // BOM para que Excel respete los acentos.
      const blob = new Blob(["\ufeff" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `${name}_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<!-- El modal base ocupa todo el ancho: el de confirmación es corto y se lee
     mejor acotado. Sin scoped porque .modal-content vive en el componente hijo. -->
<style lang="postcss">
#amazon-sync-confirm .modal-content {
  max-width: 800px;
  min-height: auto;
}
</style>
